import { useState, type ReactNode } from 'react';
import { Check, Copy, ExternalLink, KeyRound, Plug, ShieldCheck, Terminal } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { copyText } from '../lib/clipboard';
import { ConnectedAppsSection } from '../components/mcp/ConnectedApps';
import { FirmioGlyph } from '../components/brand/FirmioAiIcon';

// ---------------------------------------------------------------------------
// /mcp — Firmio MCP: what it is and how to connect a Claude client to it.
//
// The server lives at <origin>/api/mcp (routes/mcp.ts); the OAuth login happens
// on /oauth/consent. Claude Desktop and claude.ai have no install link for a
// remote connector, so the "connect" button copies the address and opens the
// Connectors settings where it is pasted. Cursor and VS Code do have install
// links, so those connect in one click. Claude Code takes one command.
// ---------------------------------------------------------------------------

const CLAUDE_CONNECTORS_URL = 'https://claude.ai/settings/connectors';

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  async function copy(key: string, text: string) {
    const ok = await copyText(text);
    setCopied(ok ? key : null);
    if (ok) window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    return ok;
  }
  return { copied, copy };
}

function CodeLine({
  value,
  copied,
  onCopy,
  label,
}: {
  value: string;
  copied: boolean;
  onCopy: () => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/60 px-3 py-2">
      <code className="min-w-0 flex-1 select-all overflow-x-auto whitespace-nowrap text-xs text-foreground">
        {value}
      </code>
      <button
        type="button"
        onClick={onCopy}
        className="flex shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-muted"
        aria-label={label}
        title={label}
      >
        {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
        {copied ? '✓' : label}
      </button>
    </div>
  );
}

function Card({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-5 rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <div>
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function McpPage() {
  const { t } = useI18n();
  const { copied, copy } = useCopy();
  const origin = window.location.origin;
  const mcpUrl = `${origin}/api/mcp`;
  const claudeCodeCmd = `claude mcp add --transport http firmio ${mcpUrl}`;
  const cursorLink = `cursor://anysphere.cursor-deeplink/mcp/install?name=firmio&config=${btoa(
    JSON.stringify({ url: mcpUrl }),
  )}`;
  const vscodeLink = `vscode:mcp/install?${encodeURIComponent(
    JSON.stringify({ name: 'firmio', type: 'http', url: mcpUrl }),
  )}`;

  async function connectClaude() {
    await copy('claude', mcpUrl);
    window.open(CLAUDE_CONNECTORS_URL, '_blank', 'noopener');
  }

  const steps = [
    t('mcp.claude.step1'),
    t('mcp.claude.step2'),
    t('mcp.claude.step3'),
    t('mcp.claude.step4'),
  ];

  return (
    <div className="mx-auto max-w-2xl p-6 sm:p-8">
      <header className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FirmioGlyph size={26} />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('mcp.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('mcp.subtitle')}</p>
        </div>
      </header>

      <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
        <p className="text-sm text-foreground">{t('mcp.intro')}</p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>{t('mcp.intro.sameTools')}</li>
          <li>{t('mcp.intro.samePermissions')}</li>
          <li>{t('mcp.intro.yourPlan')}</li>
          <li>{t('mcp.intro.noFiles')}</li>
        </ul>
      </section>

      <Card
        icon={<Plug size={18} />}
        title={t('mcp.address.title')}
        subtitle={t('mcp.address.subtitle')}
      >
        <CodeLine
          value={mcpUrl}
          copied={copied === 'url'}
          onCopy={() => void copy('url', mcpUrl)}
          label={t('mcp.copy')}
        />
      </Card>

      <Card
        icon={<FirmioGlyph size={18} />}
        title={t('mcp.claude.title')}
        subtitle={t('mcp.claude.subtitle')}
      >
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          {steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <button
          type="button"
          onClick={() => void connectClaude()}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          <ExternalLink size={16} />
          {copied === 'claude' ? t('mcp.claude.buttonCopied') : t('mcp.claude.button')}
        </button>
        <p className="mt-2 text-xs text-muted-foreground">{t('mcp.claude.buttonHint')}</p>
      </Card>

      <Card
        icon={<Terminal size={18} />}
        title={t('mcp.code.title')}
        subtitle={t('mcp.code.subtitle')}
      >
        <CodeLine
          value={claudeCodeCmd}
          copied={copied === 'code'}
          onCopy={() => void copy('code', claudeCodeCmd)}
          label={t('mcp.copy')}
        />
        <p className="mt-2 text-xs text-muted-foreground">{t('mcp.code.hint')}</p>
      </Card>

      <Card
        icon={<Terminal size={18} />}
        title={t('mcp.ide.title')}
        subtitle={t('mcp.ide.subtitle')}
      >
        <div className="flex flex-wrap gap-2">
          <a
            href={cursorLink}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            <ExternalLink size={14} />
            {t('mcp.ide.cursor')}
          </a>
          <a
            href={vscodeLink}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            <ExternalLink size={14} />
            {t('mcp.ide.vscode')}
          </a>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t('mcp.ide.hint')}</p>
      </Card>

      <Card
        icon={<ShieldCheck size={18} />}
        title={t('mcp.security.title')}
        subtitle={t('mcp.security.subtitle')}
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>{t('mcp.security.oauth')}</li>
          <li>{t('mcp.security.rbac')}</li>
          <li>{t('mcp.security.tokens')}</li>
          <li>{t('mcp.security.revoke')}</li>
          <li>{t('mcp.security.conversations')}</li>
        </ul>
      </Card>

      <Card icon={<KeyRound size={18} />} title={t('mcp.tips.title')}>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>{t('mcp.tips.context')}</li>
          <li>{t('mcp.tips.plan')}</li>
          <li>{t('mcp.tips.noAccess')}</li>
        </ul>
      </Card>

      <ConnectedAppsSection />
    </div>
  );
}
